import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { DeleteTaskResponse, TasksListResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";

/** Build the path for `DELETE /game-pass-admin/v2/admin/tasks/{task_id}` by
 *  substituting `{task_id}` in the centralised route template. The baseURL is
 *  supplied by the axios instance, so this returns just the path — matching
 *  the `API_ROUTES.X` call shape used by `useDeleteChallenge` / `useUpdateTask`. */
const buildDeleteTaskUrl = (taskId: string): string =>
  API_ROUTES.DELETE_TASK.replace("{task_id}", taskId);

/** Variables fed to `useDeleteTask.mutateAsync`. `seasonId` is optional and
 *  used purely to scope the cache update / invalidation to the season's task
 *  listing when the caller knows it. */
export type DeleteTaskVariables = {
  taskId: string;
  seasonId?: string | null;
};

const deleteTask = async ({
  taskId,
}: DeleteTaskVariables): Promise<string> => {
  // `getPrivateInstance()` already bakes the baseURL + bearer token into the
  // axios instance (via the request interceptor in
  // `PrivateAdminPanelInstance.js`), so the call site stays as terse as the
  // sibling delete hooks: pass the route and let the instance do the rest.
  // The BE returns just a success flag (no body), so we resolve to the id the
  // caller passed in for the cache update / optimistic removal.
  await getPrivateInstance().delete<DeleteTaskResponse>(
    buildDeleteTaskUrl(taskId),
  );
  return taskId;
};

/**
 * React Query mutation for `DELETE /game-pass-admin/v2/admin/tasks/{task_id}`.
 *
 * On success it optimistically drops the task from any cached tasks listing
 * (so the Season Details editor reflects the removal without waiting on a
 * refetch), then invalidates the season's task list + detail and the seasons
 * list so the per-card `task_count` metric stays in sync. Mirrors
 * `useDeleteChallenge` exactly — same invalidation shape, same optimistic
 * cache update.
 */
export const useDeleteTask = (): UseMutationResult<
  string,
  Error,
  DeleteTaskVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "tasks", "delete"],
    mutationFn: deleteTask,
    onSuccess: (taskId, variables) => {
      queryClient.setQueriesData<TasksListResponse>(
        { queryKey: ["game-pass", "tasks", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            tasks: current.tasks.filter((record) => record.id !== taskId),
          };
        },
      );
      if (variables.seasonId) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "tasks", "list", variables.seasonId],
        });
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "tasks", "list"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};

export { deleteTask };
